/* Early order-wide guidance. Native checkout validation remains the enforcement layer. */
(() => {
  'use strict';
  if (window.tfOrderLimits) return;
  const read = root => {
    const node = root.querySelector('[data-tf-order-limit-data]');
    if (!node) throw new Error('Order state unavailable');
    const data = JSON.parse(node.textContent);
    if (data.limit !== 5 || !Array.isArray(data.lines) || data.lines.some(line => !['tag', 'chain'].includes(line.kind) || !Number.isSafeInteger(line.quantity) || line.quantity < 1 || typeof line.key !== 'string')) throw new Error('Invalid order state');
    return data;
  };
  let state;
  try { state = read(document); } catch { return; }
  const totals = lines => lines.reduce((sum, line) => { sum[line.kind] += line.quantity; return sum; }, { tag: 0, chain: 0 });
  const format = (text, values) => Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`[${key}]`, String(value)), text || '');
  const quantityValidity = input => {
    input.setCustomValidity('');
    const validity = input.validity;
    const value = Number(input.value);
    let text = '';
    if (validity.badInput || validity.stepMismatch || (input.value !== '' && !Number.isSafeInteger(value))) text = state.quantity?.integer;
    else if (validity.valueMissing) text = state.quantity?.required;
    else if (validity.rangeUnderflow) text = format(state.quantity?.minimum, { min: input.min });
    else if (validity.rangeOverflow) text = Number(input.max) === 0 ? state.error : format(state.quantity?.maximum, { max: input.max });
    if (text) input.setCustomValidity(text);
    if (input.validity.valid) input.removeAttribute('aria-invalid');
    else input.setAttribute('aria-invalid', 'true');
  };
  const syncCartQuantities = () => {
    for (const form of document.querySelectorAll('form[data-tf-native-cart]')) {
      const rows = Array.from(form.querySelectorAll('[data-tf-cart-line-key]'));
      for (const row of rows) {
        const line = state.lines.find(entry => entry.key === row.dataset.tfCartLineKey);
        const input = row.querySelector('[name="updates[]"]');
        if (!line || !input) continue;
        // Count pending edits in the other rows, including zero for removal.
        const others = rows.reduce((count, other) => {
          if (other === row) return count;
          const saved = state.lines.find(entry => entry.key === other.dataset.tfCartLineKey);
          if (!saved || saved.kind !== line.kind) return count;
          const field = other.querySelector('[name="updates[]"]');
          const value = Number(field?.value);
          return count + (field?.value !== '' && Number.isSafeInteger(value) && value >= 0 ? value : saved.quantity);
        }, 0);
        input.max = String(Math.max(0, state.limit - others));
        quantityValidity(input);
      }
    }
  };
  const message = (form, text) => {
    const region = form.querySelector('[data-tf-order-error]');
    if (!region) return;
    region.querySelector('[data-tf-status-message]').textContent = text;
    region.hidden = !text;
    if (text) { region.tabIndex = -1; region.focus({ preventScroll: true }); region.scrollIntoView({ block: 'nearest' }); }
  };
  const sync = () => {
    const count = totals(state.lines);
    for (const form of document.querySelectorAll('form[data-tf-order-kind]')) {
      const kind = form.dataset.tfOrderKind;
      const existing = state.lines.find(line => line.key === form.dataset.tfCartKey);
      const input = form.querySelector('[name="quantity"]');
      const allowance = Math.max(0, state.limit - count[kind] + (existing?.quantity || 0));
      const remaining = input?.hasAttribute('data-tf-native-max') ? Math.min(allowance, Number(input.dataset.tfNativeMax)) : allowance;
      const changed = input && input.max !== String(remaining);
      if (input) input.max = String(remaining);
      const note = form.querySelector('[data-tf-order-remaining]');
      if (note) {
        note.textContent = format(state.remaining?.[kind], { count: count[kind], remaining: allowance });
        note.hidden = !!existing || count[kind] === 0 || !note.textContent;
      }
      form.toggleAttribute('data-tf-order-full', remaining === 0);
      if (changed && form.hasAttribute('data-tf-engraving-form')) input.dispatchEvent(new Event('input', { bubbles: true }));
      if (kind === 'chain') {
        const select = form.querySelector('[data-tf-quantity-choice]');
        if (select) {
          for (const option of select.options) option.disabled = Number(option.value) > remaining;
          select.disabled = remaining < Number(input.min || 1);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = remaining === 0 || form.dataset.tfAvailable !== 'true'; });
      }
      if (remaining === 0) {
        form.querySelectorAll('[data-tf-add],button[type="submit"]').forEach(button => { button.disabled = true; });
        message(form, state.error);
      }
      if (input) quantityValidity(input);
    }
    syncCartQuantities();
  };
  let pending;
  const refresh = () => {
    if (pending) return pending;
    pending = (async () => {
      const response = await fetch(state.url, { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Order state unavailable');
      state = read(new DOMParser().parseFromString(await response.text(), 'text/html'));
      sync();
      return state;
    })().finally(() => { pending = null; });
    return pending;
  };
  const check = async form => {
    try {
      await refresh();
      let lines;
      if (form.matches('[data-tf-native-cart]')) {
        const rows = Array.from(form.querySelectorAll('[data-tf-cart-line-key]'));
        if (rows.length !== state.lines.length || rows.some(row => !state.lines.some(line => line.key === row.dataset.tfCartLineKey))) throw new Error('Cart changed');
        lines = rows.map(row => ({ kind: row.dataset.tfOrderKind, quantity: Number(row.querySelector('[name="updates[]"]').value) }));
      } else {
        lines = state.lines.filter(line => line.key !== form.dataset.tfCartKey);
        lines.push({ kind: form.dataset.tfOrderKind, quantity: Number(form.querySelector('[name="quantity"]').value) });
      }
      const count = totals(lines);
      const minimum = form.matches('[data-tf-native-cart]') ? 0 : 1;
      if (lines.some(line => !['tag', 'chain'].includes(line.kind) || !Number.isSafeInteger(line.quantity) || line.quantity < minimum) || count.tag > state.limit || count.chain > state.limit) {
        message(form, state.error);
        return false;
      }
      message(form, '');
      return true;
    } catch {
      message(form, state.unavailable);
      return false;
    }
  };
  const ready = new WeakSet();
  const busy = new WeakSet();
  const submitApproved = (form, submitter) => {
    ready.add(form);
    if (submitter) form.requestSubmit(submitter);
    else form.requestSubmit();
    ready.delete(form);
  };
  window.tfOrderLimits = Object.freeze({ check, refresh, submitApproved });
  sync();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('form[data-tf-order-kind],form[data-tf-native-cart]') || event.defaultPrevented) return;
    if (ready.has(form)) { ready.delete(form); return; }
    event.preventDefault();
    if (busy.has(form)) return;
    busy.add(form);
    form.setAttribute('aria-busy', 'true');
    const submitter = event.submitter;
    const valid = await check(form);
    busy.delete(form);
    form.removeAttribute('aria-busy');
    if (!valid || !form.isConnected) return;
    // Native validation can stop requestSubmit before another submit event.
    submitApproved(form, submitter);
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[name="quantity"],[name="updates[]"]')) return;
    const form = event.target.closest('form');
    if (form) message(form, '');
    if (event.target.matches('[name="updates[]"]')) syncCartQuantities();
    else quantityValidity(event.target);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-tf-quantity-choice]')) {
      const input = event.target.closest('form')?.querySelector('[name="quantity"]');
      if (input) quantityValidity(input);
    }
  });
  document.addEventListener('invalid', event => {
    if (event.target.matches('[name="quantity"],[name="updates[]"]')) quantityValidity(event.target);
  }, true);
  document.addEventListener('shopify:section:load', () => {
    try { state = read(document); } catch { /* Retain the last usable cart state. */ }
    sync();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) refresh().catch(() => {});
  });
})();
